/**
 * The one timezone picker every screen uses, so a zone is named the same way everywhere.
 *
 * 1. `getTimezoneStrings` builds the full IANA list once per locale.
 * 2. `additionalTimezones` prepends the caller's own entries to that list. Each entry carries a
 *    `group`, which becomes the option's `category`.
 * 3. `groupBy` reads `category`, but only when the caller passes at least one additional timezone.
 *    Without one the control renders the plain, heading-free list it always did.
 *
 * The step order is the contract: the caller's entries lead, so a reader finds them without a
 * search. An additional timezone is a normal IANA id, so picking one is indistinguishable from
 * picking it out of the full list. `AvailabilityControls.tsx` is the caller that needs the
 * headings: it offers the interviewer's own zone above the viewer's. Continues in `index.ts`.
 */
import { TimezoneOption, getTimezoneStrings } from '#datetime';
import { WorldWithClockIcon } from '#icons';
import { useTranslation } from '@modernloop/i18n';
import { createFilterOptions, TextField, Tooltip } from '@mui/material';
import { Autocomplete } from '@mui/material';
import { FC, ReactNode, useMemo } from 'react';

/**
 * One extra timezone the caller wants at the top of the list, under its own heading — for example
 * the interviewer's own zone on the availability screen. The value is a normal IANA id, so picking
 * one is indistinguishable from picking it out of the full list.
 */
export type AdditionalTimezoneOption = {
  readonly label: string;
  readonly value: string;
  /** The heading this option sits under. Options that share a heading render together. */
  readonly group: string;
};

/**
 * Props for the controlled timezone picker.
 *
 * The optional start adornment lets one caller use a domain-specific glyph while preserving the
 * existing clock glyph for every other caller.
 */
interface Props {
  onTimezoneChange: (timezone: string) => void;
  value: string;
  fullWidth?: boolean;
  disableClearable?: boolean;
  /**
   * Timezones to offer above the full list, grouped by `group`. When present the list gains
   * headings; without it the control renders exactly as before.
   */
  additionalTimezones?: readonly AdditionalTimezoneOption[];
  /** Optional replacement for the default clock glyph at the start of the field. */
  startAdornment?: ReactNode;
  label?: string;
  size?: 'small' | 'medium';
  dataTestId?: string;
}

/** Renders a searchable, controlled IANA timezone picker with optional grouped entries. */
export const TimezoneSelect: FC<Props> = ({
  onTimezoneChange,
  value,
  fullWidth = true,
  disableClearable,
  additionalTimezones,
  startAdornment = <WorldWithClockIcon />,
  label,
  size,
  dataTestId,
}) => {
  const { t } = useTranslation();
  const nullOption: TimezoneOption = useMemo(
    () => ({ label: t('common:shared.timezone.setTimezone'), value: '', keywords: '', category: '' }),
    [t]
  );
  const standardOptions = useMemo(() => getTimezoneStrings(t), [t]);
  // The caller's own entries lead, each under its heading; the full list follows under its own.
  const timezoneOptions = useMemo(
    () => [
      ...(additionalTimezones ?? []).map((option): TimezoneOption => ({
        label: option.label,
        value: option.value,
        keywords: '',
        category: option.group,
      })),
      ...standardOptions,
    ],
    [additionalTimezones, standardOptions]
  );
  // Derive the selected option from props during render — no effect/state, so option or
  // nullOption identity changes can't feed a setState->re-render loop.
  const displayValue = timezoneOptions.find((option) => option.value === value) ?? nullOption;
  const getOptionLabel = (option: TimezoneOption) => option.label;

  const filterOptions = createFilterOptions({
    stringify: (tz: TimezoneOption) => `${tz.label},${tz.value},${tz.keywords}`,
  });

  return (
    <Autocomplete
      disableClearable={disableClearable}
      value={displayValue}
      // Match by timezone id, not object identity, so the controlled value resolves against
      // the (rebuilt-per-locale) option list without MUI "value is invalid" warnings.
      isOptionEqualToValue={(option, selected) => option.value === selected.value}
      getOptionLabel={getOptionLabel}
      filterOptions={filterOptions}
      renderInput={(params) => (
        <Tooltip title={value}>
          <TextField
            {...params}
            label={label}
            InputProps={{
              ...params.InputProps,
              startAdornment,
            }}
            value={value}
          />
        </Tooltip>
      )}
      options={timezoneOptions}
      groupBy={additionalTimezones?.length ? (option) => option.category : undefined}
      size={size}
      data-testid={dataTestId}
      onChange={(_event, value) => {
        onTimezoneChange(value?.value || '');
      }}
      // eslint-disable-next-line no-restricted-syntax
      sx={{ width: fullWidth ? '100%' : '300px' }}
    />
  );
};
